import { cn } from "@/lib/utils";
import { Skeleton } from "../ui/skeleton";

/** Right-aligned bubble mirroring a user message while the thread loads. */
function UserBubbleSkeleton({ lineWidths }: { lineWidths: readonly string[] }) {
  return (
    <div className="flex justify-end w-full">
      <div className="flex flex-col gap-2 items-end px-4 py-3 max-w-[80%] rounded-2xl rounded-br-sm bg-majorelle/10">
        {lineWidths.map((width, i) => (
          <Skeleton key={i} className={cn("h-3.5 bg-majorelle/30", width)} />
        ))}
      </div>
    </div>
  );
}

/** Full-width text lines mirroring an assistant message while the thread loads. */
function AssistantLinesSkeleton({
  lineWidths,
}: {
  lineWidths: readonly string[];
}) {
  return (
    <div className="flex flex-col gap-2.5 py-1 w-full">
      {lineWidths.map((width, i) => (
        <Skeleton key={i} className={cn("h-3.5", width)} />
      ))}
    </div>
  );
}

/**
 * Loading placeholder for an existing conversation. Reproduces the message-list
 * layout (centered column, right-aligned user bubbles, left-aligned assistant
 * text) so the skeleton reads as a chat instead of generic lines.
 */
export function ChatConversationSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <div
      aria-hidden="true"
      className={cn("mx-auto w-full max-w-3xl", className)}
    >
      <div className="flex flex-col px-3 py-6 space-y-8">
        <UserBubbleSkeleton lineWidths={["w-40"]} />
        <AssistantLinesSkeleton lineWidths={["w-[85%]", "w-[92%]", "w-[55%]"]} />
        <UserBubbleSkeleton lineWidths={["w-56", "w-32"]} />
        <AssistantLinesSkeleton
          lineWidths={["w-[78%]", "w-[88%]", "w-[95%]", "w-[40%]"]}
        />
        <UserBubbleSkeleton lineWidths={["w-44"]} />
        <AssistantLinesSkeleton lineWidths={["w-[90%]", "w-[62%]"]} />
      </div>
    </div>
  );
}
